import { useState } from "react";
import { useLocation } from "react-router-dom";

import { NetworkStatus, useLazyQuery } from "@apollo/client";

import { useDateRange } from "../../../../common/components/datePicker/modelsDatePickers";
import { useCustomerIdGuard } from "../../../../common/hooks/useCustomerIdGuard";
import { RouteEnum } from "../../../../common/models/enums";
import { GetEventsByModelAndFacilityParams } from "../../../../common/types/events/params";
import { RecordingsApiResponse } from "../../../../common/types/recordings/api";
import { buildEventQueryVariables } from "../../../../common/utils/buildEventQueryVariables";
import { handleApolloError } from "../../../../common/utils/handleApolloError";
import { errorNotification } from "../../../../common/variables/notification";
import {
  useSelectedLocation,
  useSelectedTagLocationIds,
} from "../../../../common/variables/selectedLocation";
import { useSelectedModel } from "../../../../common/variables/selectedModel";
import { GET_RECORDINGS_BY_MODEL_AND_FACILITY } from "../../../../rest/queries";
import { useSelectedDataHubCamera } from "../../../data-hub/components/dataHub.variable";
import { mapRecordingEntityToViewModel } from "../../adapters/recordingAdapter";

const DEFAULT_PAGE_SIZE = 500;

export const HARDHAT_SELECT_OPTION = { title: "Hard hat", value: "HARDHAT" };

export const useGetRecordingsToExport = () => {
  const { pathname } = useLocation();

  const customerId = useCustomerIdGuard();
  const facilityId = useSelectedLocation();
  const tagLocationIds = useSelectedTagLocationIds();
  const dateRange = useDateRange();
  let modelId = useSelectedModel();
  const camera = useSelectedDataHubCamera();

  if (
    pathname === RouteEnum.TelemetryHardHat ||
    pathname === RouteEnum.SensoryTelemetryHardHat
  ) {
    modelId = HARDHAT_SELECT_OPTION;
  }

  const params: GetEventsByModelAndFacilityParams = {
    customerId,
    modelId: modelId?.value.toLowerCase() ?? "",
    facilityId: facilityId?.value ?? "",
    fromDate: dateRange?.from ? dateRange.from.toISOString() : undefined,
    toDate: dateRange?.to ? dateRange.to.toISOString() : undefined,
    pageSize: DEFAULT_PAGE_SIZE,
  };

  const variables = buildEventQueryVariables(params);

  const [loading, setLoading] = useState(false);

  const [fetchRecordings, { error, fetchMore, networkStatus }] = useLazyQuery<{
    recordings: RecordingsApiResponse;
  }>(GET_RECORDINGS_BY_MODEL_AND_FACILITY, {
    variables: {
      ...variables,
      modelId: modelId?.value.toLowerCase() ?? "",
    },
    fetchPolicy: "network-only",
    onError: handleApolloError,
  });

  const getRecordings = async () => {
    setLoading(true);

    try {
      const initialResponse = await fetchRecordings({
        variables: {
          ...variables,
          lastEvaluatedKey: "",
        },
      });

      let allRecordings = initialResponse.data?.recordings?.data ?? [];

      if (allRecordings.length === 0) {
        setLoading(false);

        errorNotification("No recordings found for export");

        return [];
      }

      let currentLastEvaluatedKey =
        initialResponse.data?.recordings?.lastEvaluatedKey;

      // Continue fetching until there are no more pages
      while (currentLastEvaluatedKey) {
        const moreResponse = await fetchMore({
          variables: {
            ...variables,
            pageSize: DEFAULT_PAGE_SIZE,
            lastEvaluatedKey: encodeURIComponent(
              JSON.stringify(currentLastEvaluatedKey)
            ),
          },
        });

        const newRecordings = moreResponse.data?.recordings?.data ?? [];

        allRecordings = [...allRecordings, ...newRecordings];

        currentLastEvaluatedKey =
          moreResponse.data?.recordings?.lastEvaluatedKey;
      }

      let mappedRecordings = allRecordings.map(mapRecordingEntityToViewModel);

      // Apply client-side device filter to align with Camera filter dropdown
      const selectedCamera = (camera?.value ?? "").replace(/^DE#/, "");

      if (selectedCamera) {
        mappedRecordings = mappedRecordings.filter(r => {
          const rawCameraId =
            r?.rawEventInfo?.data?.camera_id ||
            r?.rawEventInfo?.data?.instance_name ||
            "";

          const normalized = rawCameraId.replace(/^DE#/, "");

          return normalized === selectedCamera;
        });
      }

      // Apply tag location filter if a tag is selected
      if (tagLocationIds && tagLocationIds.length > 0) {
        const normalizedTagLocationIds = tagLocationIds.map(id =>
          id.toLowerCase()
        );

        mappedRecordings = mappedRecordings.filter(r => {
          const location = r?.rawEventInfo?.location || r?.facilityId || "";
          const normalizedLocation = location.toLowerCase();

          return normalizedTagLocationIds.includes(normalizedLocation);
        });
      }

      setLoading(false);

      return mappedRecordings;
    } catch (error) {
      console.error("Failed to fetch all recordings for export:", error);

      errorNotification("Failed to fetch recordings for export");

      setLoading(false);

      return [];
    }
  };

  return {
    getRecordings,
    loading: loading || networkStatus === NetworkStatus.fetchMore,
    error,
    fetchMore,
  } as const;
};
